import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  KoraxPage,
  KoraxPageHeader
} from "../../components/KoraxUI";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background:
      theme.palette.type === "dark"
        ? "#080E1C"
        : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles
  },

  inner: {
    width: "100%",
    maxWidth: 1440,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 18
  },

  backButton: {
    border: 0,
    background: "transparent",
    padding: 0,
    color: "#2563EB",
    fontWeight: 800,
    fontSize: "0.74rem",
    cursor: "pointer"
  },

  hero: {
    position: "relative",
    overflow: "hidden",
    padding: "26px 28px",
    borderRadius: 24,
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg,#071329 0%,#0B2C68 52%,#155EEF 100%)",
    boxShadow:
      "0 22px 48px rgba(15,43,94,.22)"
  },

  heroEyebrow: {
    color: "#BFDBFE",
    fontSize: "0.67rem",
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".12em"
  },

  heroTitle: {
    margin: "8px 0 0",
    fontSize: "clamp(1.55rem,3vw,2.35rem)",
    fontWeight: 900,
    letterSpacing: "-.045em"
  },

  heroSubtitle: {
    maxWidth: 720,
    marginTop: 10,
    color: "rgba(219,234,254,.82)",
    fontSize: "0.79rem",
    lineHeight: 1.65
  },

  refreshButton: {
    marginTop: 18,
    minHeight: 38,
    padding: "8px 14px",
    borderRadius: 11,
    border: "1px solid rgba(255,255,255,.20)",
    background: "rgba(255,255,255,.10)",
    color: "#FFFFFF",
    fontFamily: "inherit",
    fontSize: "0.72rem",
    fontWeight: 850,
    cursor: "pointer"
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(6,minmax(0,1fr))",
    gap: 12,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns:
        "repeat(3,minmax(0,1fr))"
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns:
        "repeat(2,minmax(0,1fr))"
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  card: {
    padding: 18,
    borderRadius: 17,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)"
  },

  kpiLabel: {
    color:
      theme.palette.type === "dark"
        ? "#94A3B8"
        : "#64748B",
    fontSize: "0.64rem",
    textTransform: "uppercase",
    fontWeight: 850,
    letterSpacing: ".055em"
  },

  kpiValue: {
    marginTop: 8,
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0F172A",
    fontSize: "1.65rem",
    fontWeight: 900
  },

  rateGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(3,minmax(0,1fr))",
    gap: 12,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  sectionTitle: {
    margin: 0,
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0F172A",
    fontSize: "1rem",
    fontWeight: 900
  },

  sectionSubtitle: {
    marginTop: 5,
    color:
      theme.palette.type === "dark"
        ? "#94A3B8"
        : "#64748B",
    fontSize: "0.72rem"
  },

  chartWrap: {
    height: 330,
    marginTop: 18
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
    marginTop: 16
  },

  table: {
    width: "100%",
    minWidth: 920,
    borderCollapse: "collapse"
  },

  th: {
    padding: "10px 12px",
    textAlign: "left",
    color:
      theme.palette.type === "dark"
        ? "#94A3B8"
        : "#64748B",
    fontSize: "0.64rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.08)"
        : "#E8EDF5"
    }`
  },

  td: {
    padding: "13px 12px",
    color:
      theme.palette.type === "dark"
        ? "#E2E8F0"
        : "#334155",
    fontSize: "0.72rem",
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.055)"
        : "#EEF2F7"
    }`
  },

  campaignName: {
    fontWeight: 850,
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0F172A"
  },

  companyName: {
    fontWeight: 850,
    cursor: "pointer",
    color: "#2563EB"
  },

  sourceBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 8px",
    borderRadius: 999,
    background: "rgba(37,99,235,.10)",
    color: "#2563EB",
    fontWeight: 850,
    fontSize: "0.63rem"
  },

  funnel: {
    display: "flex",
    gap: 5,
    flexWrap: "wrap"
  },

  funnelStep: {
    width: 25,
    height: 25,
    borderRadius: 7,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.68rem",
    fontWeight: 900
  },

  funnelDone: {
    color: "#15803D",
    background: "rgba(34,197,94,.12)"
  },

  funnelPending: {
    color:
      theme.palette.type === "dark"
        ? "#64748B"
        : "#94A3B8",
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.04)"
        : "#F1F5F9"
  },

  filterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 16,
    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.07)"
        : "#E8EDF5"
    }`
  },

  filterButtons: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap"
  },

  filterButton: {
    minHeight: 34,
    padding: "7px 11px",
    borderRadius: 9,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.08)"
        : "#E2E8F0"
    }`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "#F8FAFC",
    color:
      theme.palette.type === "dark"
        ? "#CBD5E1"
        : "#475569",
    fontFamily: "inherit",
    fontSize: "0.68rem",
    fontWeight: 850,
    cursor: "pointer"
  },

  filterButtonActive: {
    color: "#FFFFFF",
    borderColor: "#2563EB",
    background:
      "linear-gradient(135deg,#2563EB,#4F46E5)",
    boxShadow:
      "0 7px 16px rgba(37,99,235,.18)"
  },

  customDates: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap"
  },

  dateInput: {
    minHeight: 34,
    padding: "6px 9px",
    borderRadius: 9,
    outline: "none",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.10)"
        : "#DCE3ED"
    }`,
    background:
      theme.palette.type === "dark"
        ? "#111C2E"
        : "#FFFFFF",
    color:
      theme.palette.type === "dark"
        ? "#FFFFFF"
        : "#0F172A",
    fontFamily: "inherit",
    fontSize: "0.7rem"
  },

  applyButton: {
    minHeight: 34,
    padding: "7px 12px",
    border: 0,
    borderRadius: 9,
    color: "#FFFFFF",
    background: "#2563EB",
    fontFamily: "inherit",
    fontSize: "0.68rem",
    fontWeight: 850,
    cursor: "pointer"
  },

  metadata: {
    marginTop: 5,
    color:
      theme.palette.type === "dark"
        ? "#64748B"
        : "#94A3B8",
    fontSize: "0.61rem",
    lineHeight: 1.5
  },

  breakdownBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 7px",
    borderRadius: 7,
    color: "#4338CA",
    background: "rgba(99,102,241,.10)",
    fontSize: "0.61rem",
    fontWeight: 850
  },

  funnelLabels: {
    display: "flex",
    gap: 5,
    flexWrap: "wrap"
  },

  funnelLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 7px",
    borderRadius: 8,
    fontSize: "0.59rem",
    fontWeight: 800
  },

  empty: {
    padding: "34px 20px",
    textAlign: "center",
    color:
      theme.palette.type === "dark"
        ? "#94A3B8"
        : "#64748B",
    fontSize: "0.78rem"
  }
}));

const formatNumber = value =>
  new Intl.NumberFormat("pt-BR").format(
    Number(value || 0)
  );

const DashboardMasterAcquisition = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] =
    useState(true);

  const [range, setRange] =
    useState("30d");

  const [dateFrom, setDateFrom] =
    useState("");

  const [dateTo, setDateTo] =
    useState("");

  const isSuper =
    user?.super === true ||
    user?.profile === "super";

  const loadData = useCallback(async () => {
    if (!isSuper) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = {
        range
      };

      if (
        range === "custom" &&
        dateFrom &&
        dateTo
      ) {
        params.from = dateFrom;
        params.to = dateTo;
      }

      const response = await api.get(
        "/dashboard-master/acquisition",
        {
          params
        }
      );

      setData(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, [
    isSuper,
    range,
    dateFrom,
    dateTo
  ]);

  useEffect(() => {
    if (
      range === "custom" &&
      (!dateFrom || !dateTo)
    ) {
      return;
    }

    loadData();
  }, [
    loadData,
    range,
    dateFrom,
    dateTo
  ]);

  const summary = data?.summary || {};

  const funnelData = useMemo(
    () => [
      {
        name: "Cadastros",
        value: Number(
          summary.registrations || 0
        )
      },
      {
        name: "IA",
        value: Number(
          summary.agentsConfigured || 0
        )
      },
      {
        name: "WhatsApp",
        value: Number(
          summary.whatsappsConnected || 0
        )
      },
      {
        name: "Onboarding",
        value: Number(
          summary.onboardingsCompleted || 0
        )
      },
      {
        name: "Ativados",
        value: Number(
          summary.activatedUsers || 0
        )
      },
      {
        name: "Compras",
        value: Number(
          summary.purchases || 0
        )
      }
    ],
    [summary]
  );

  if (!isSuper) {
    return (
      <div className={classes.root}>
        <KoraxPage>
          <div className={classes.inner}>
            <div className={classes.card}>
              Acesso restrito ao usuário Master.
            </div>
          </div>
        </KoraxPage>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <KoraxPage>
        <div className={classes.inner}>
          <button
            type="button"
            className={classes.backButton}
            onClick={() =>
              history.push(
                "/dashboard-master"
              )
            }
          >
            ← Voltar ao Dashboard Master
          </button>

          <div className={classes.hero}>
            <div className={classes.heroEyebrow}>
              Korax Control Center
            </div>

            <h1 className={classes.heroTitle}>
              Aquisição
            </h1>

            <div className={classes.heroSubtitle}>
              Acompanhe da origem do cadastro
              até ativação e compra. Os dados
              abaixo são calculados pelo próprio
              funil interno da Korax.
            </div>

            <button
              type="button"
              className={classes.refreshButton}
              onClick={loadData}
              disabled={loading}
            >
              {loading
                ? "Atualizando..."
                : "Atualizar dados"}
            </button>
          </div>

          <div className={classes.filterBar}>
            <div className={classes.filterButtons}>
              {[
                ["today", "Hoje"],
                ["7d", "7 dias"],
                ["30d", "30 dias"],
                ["month", "Este mês"],
                ["all", "Todo período"],
                ["custom", "Personalizado"]
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`${classes.filterButton} ${
                    range === value
                      ? classes.filterButtonActive
                      : ""
                  }`}
                  onClick={() =>
                    setRange(value)
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {range === "custom" && (
              <div className={classes.customDates}>
                <input
                  type="date"
                  className={classes.dateInput}
                  value={dateFrom}
                  onChange={event =>
                    setDateFrom(
                      event.target.value
                    )
                  }
                />

                <span>até</span>

                <input
                  type="date"
                  className={classes.dateInput}
                  value={dateTo}
                  onChange={event =>
                    setDateTo(
                      event.target.value
                    )
                  }
                />

                <button
                  type="button"
                  className={classes.applyButton}
                  onClick={loadData}
                  disabled={
                    !dateFrom ||
                    !dateTo ||
                    loading
                  }
                >
                  Aplicar
                </button>
              </div>
            )}
          </div>

          <div className={classes.kpiGrid}>
            {[
              [
                "Cadastros",
                summary.registrations
              ],
              [
                "IA configurada",
                summary.agentsConfigured
              ],
              [
                "WhatsApp conectado",
                summary.whatsappsConnected
              ],
              [
                "Onboarding",
                summary.onboardingsCompleted
              ],
              [
                "Ativados",
                summary.activatedUsers
              ],
              [
                "Compras",
                summary.purchases
              ]
            ].map(([label, value]) => (
              <div
                key={label}
                className={classes.card}
              >
                <div
                  className={
                    classes.kpiLabel
                  }
                >
                  {label}
                </div>

                <div
                  className={
                    classes.kpiValue
                  }
                >
                  {formatNumber(value)}
                </div>
              </div>
            ))}
          </div>

          <div className={classes.rateGrid}>
            {[
              [
                "Cadastro → Ativação",
                summary.registrationToActivationRate
              ],
              [
                "Ativação → Compra",
                summary.activationToPurchaseRate
              ],
              [
                "Cadastro → Compra",
                summary.registrationToPurchaseRate
              ]
            ].map(([label, value]) => (
              <div
                key={label}
                className={classes.card}
              >
                <div
                  className={
                    classes.kpiLabel
                  }
                >
                  {label}
                </div>

                <div
                  className={
                    classes.kpiValue
                  }
                >
                  {Number(value || 0)
                    .toFixed(2)}
                  %
                </div>
              </div>
            ))}
          </div>

          <div className={classes.card}>
            <h2 className={classes.sectionTitle}>
              Funil de aquisição
            </h2>

            <div
              className={
                classes.sectionSubtitle
              }
            >
              Evolução dos usuários entre os
              principais marcos de ativação.
            </div>

            <div className={classes.chartWrap}>
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={funnelData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 0,
                    bottom: 10
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="rgba(148,163,184,.18)"
                  />

                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 11,
                      fill: "#64748B"
                    }}
                  />

                  <YAxis
                    allowDecimals={false}
                    tick={{
                      fontSize: 11,
                      fill: "#64748B"
                    }}
                  />

                  <Tooltip />

                  <Bar
                    dataKey="value"
                    fill="#2563EB"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={classes.card}>
            <h2 className={classes.sectionTitle}>
              Campanhas
            </h2>

            <div
              className={
                classes.sectionSubtitle
              }
            >
              Performance por origem,
              mídia e campanha.
            </div>

            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Campanha
                    </th>
                    <th className={classes.th}>
                      Origem
                    </th>
                    <th className={classes.th}>
                      Conjuntos
                    </th>
                    <th className={classes.th}>
                      Anúncios
                    </th>
                    <th className={classes.th}>
                      Cadastros
                    </th>
                    <th className={classes.th}>
                      IA
                    </th>
                    <th className={classes.th}>
                      WhatsApp
                    </th>
                    <th className={classes.th}>
                      Ativados
                    </th>
                    <th className={classes.th}>
                      Compras
                    </th>
                    <th className={classes.th}>
                      Cad. → Ativ.
                    </th>
                    <th className={classes.th}>
                      Ativ. → Compra
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(data?.campaigns || []).map(
                    (row, index) => (
                      <tr
                        key={`${row.source}-${row.campaign}-${index}`}
                      >
                        <td
                          className={
                            classes.td
                          }
                        >
                          <span
                            className={
                              classes.campaignName
                            }
                          >
                            {row.campaign}
                          </span>

                          {row.campaignId && (
                            <div
                              className={
                                classes.metadata
                              }
                            >
                              ID: {row.campaignId}
                            </div>
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {row.source} /{" "}
                          {row.medium}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.adsetsCount
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.adsCount
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.registrations
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.agentsConfigured
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.whatsappsConnected
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.activatedUsers
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {formatNumber(
                            row.purchases
                          )}
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {Number(
                            row.registrationToActivationRate ||
                              0
                          ).toFixed(2)}
                          %
                        </td>

                        <td
                          className={
                            classes.td
                          }
                        >
                          {Number(
                            row.activationToPurchaseRate ||
                              0
                          ).toFixed(2)}
                          %
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {!loading &&
              !(data?.campaigns || [])
                .length && (
                <div className={classes.empty}>
                  Nenhuma campanha atribuída
                  ainda.
                </div>
              )}
          </div>

          <div className={classes.card}>
            <h2 className={classes.sectionTitle}>
              Campanhas, conjuntos e anúncios
            </h2>

            <div
              className={
                classes.sectionSubtitle
              }
            >
              Detalhamento da origem de cada
              cadastro até o nível do anúncio
              e do criativo.
            </div>

            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Campanha
                    </th>
                    <th className={classes.th}>
                      Conjunto
                    </th>
                    <th className={classes.th}>
                      Anúncio
                    </th>
                    <th className={classes.th}>
                      Criativo
                    </th>
                    <th className={classes.th}>
                      Cadastros
                    </th>
                    <th className={classes.th}>
                      IA
                    </th>
                    <th className={classes.th}>
                      WhatsApp
                    </th>
                    <th className={classes.th}>
                      Onboarding
                    </th>
                    <th className={classes.th}>
                      Ativados
                    </th>
                    <th className={classes.th}>
                      Compras
                    </th>
                    <th className={classes.th}>
                      Cad. → Ativ.
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(data?.breakdown || []).map(
                    (row, index) => (
                      <tr
                        key={`${row.campaignId || row.campaign}-${row.adsetId || "x"}-${row.adId || index}`}
                      >
                        <td className={classes.td}>
                          <span
                            className={
                              classes.campaignName
                            }
                          >
                            {row.campaign}
                          </span>

                          {row.campaignId && (
                            <div
                              className={
                                classes.metadata
                              }
                            >
                              {row.campaignId}
                            </div>
                          )}
                        </td>

                        <td className={classes.td}>
                          {row.adsetId ? (
                            <span
                              className={
                                classes.breakdownBadge
                              }
                            >
                              {row.adsetId}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className={classes.td}>
                          {row.adId ? (
                            <span
                              className={
                                classes.breakdownBadge
                              }
                            >
                              {row.adId}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td className={classes.td}>
                          {row.content || "—"}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.registrations
                          )}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.agentsConfigured
                          )}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.whatsappsConnected
                          )}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.onboardingsCompleted
                          )}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.activatedUsers
                          )}
                        </td>

                        <td className={classes.td}>
                          {formatNumber(
                            row.purchases
                          )}
                        </td>

                        <td className={classes.td}>
                          {Number(
                            row.registrationToActivationRate ||
                              0
                          ).toFixed(2)}
                          %
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {!loading &&
              !(data?.breakdown || [])
                .length && (
                <div className={classes.empty}>
                  Nenhum anúncio atribuído
                  neste período.
                </div>
              )}
          </div>

          <div className={classes.card}>
            <h2 className={classes.sectionTitle}>
              Empresas adquiridas
            </h2>

            <div
              className={
                classes.sectionSubtitle
              }
            >
              Empresas com origem identificada
              e estágio atual dentro do funil.
            </div>

            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>
                      Empresa
                    </th>
                    <th className={classes.th}>
                      Origem
                    </th>
                    <th className={classes.th}>
                      Campanha
                    </th>
                    <th className={classes.th}>
                      Criativo
                    </th>
                    <th className={classes.th}>
                      Funil
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(data?.companies || []).map(
                    company => {
                      const steps = [
                        [
                          "Cadastro",
                          company.registrationAt
                        ],
                        [
                          "IA",
                          company.agentConfiguredAt
                        ],
                        [
                          "WhatsApp",
                          company.whatsappConnectedAt
                        ],
                        [
                          "Onboarding",
                          company.onboardingCompletedAt
                        ],
                        [
                          "Ativado",
                          company.activatedAt
                        ],
                        [
                          "Compra",
                          company.purchaseAt
                        ]
                      ];

                      return (
                        <tr
                          key={
                            company.companyId
                          }
                        >
                          <td
                            className={
                              classes.td
                            }
                          >
                            <span
                              className={
                                classes.companyName
                              }
                              onClick={() =>
                                history.push(
                                  `/dashboard-master/companies/${company.companyId}`
                                )
                              }
                            >
                              {
                                company.companyName
                              }
                            </span>

                            <div
                              style={{
                                marginTop: 4,
                                opacity: 0.65,
                                fontSize:
                                  "0.64rem"
                              }}
                            >
                              {company.email}
                            </div>
                          </td>

                          <td
                            className={
                              classes.td
                            }
                          >
                            <span
                              className={
                                classes.sourceBadge
                              }
                            >
                              {company.source ||
                                "direct"}
                              {" / "}
                              {company.medium ||
                                "unknown"}
                            </span>
                          </td>

                          <td
                            className={
                              classes.td
                            }
                          >
                            {company.campaign ||
                              "(sem campanha)"}
                          </td>

                          <td
                            className={
                              classes.td
                            }
                          >
                            {company.content ||
                              "—"}
                          </td>

                          <td
                            className={
                              classes.td
                            }
                          >
                            <div
                              className={
                                classes.funnel
                              }
                              title="Cadastro · IA · WhatsApp · Onboarding · Ativado · Compra"
                            >
                              {steps.map(
                                (
                                  [label, done],
                                  index
                                ) => (
                                  <span
                                    key={
                                      index
                                    }
                                    className={`${
                                      classes.funnelLabel
                                    } ${
                                      done
                                        ? classes.funnelDone
                                        : classes.funnelPending
                                    }`}
                                  >
                                    <strong>
                                      {done
                                        ? "✓"
                                        : "—"}
                                    </strong>

                                    {label}
                                  </span>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </KoraxPage>
    </div>
  );
};

export default DashboardMasterAcquisition;
